import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import Modal from '../../Components/Modal'
import {
    StaffSelector,
    StatusSelector,
    CostCentreSelector,
} from '../../Components/Selector'
import _ from 'lodash'
import StaffCollection from '../../State/Collections/StaffCollection'
import { trpc } from '../../system/trpc' // Import tRPC client
import CostCentreCollection from '../../State/Collections/CostCentreCollection'
import ProjectCollection from '../../State/Collections/ProjectCollection'
import PhaseCollection from '../../State/Collections/PhaseCollection'
import ProjectListStore from './ProjectListStore'

const BatchEditProjectModal = observer(
    ({ modalId, selectedProjectIds = [], selectedPhaseIds = [] }) => {
        const [status, setStatus] = useState('') // Use empty string to represent "no change"
        const [ownerId, setOwnerId] = useState('') // Use empty string for "no change"
        const [costCentreId, setCostCentreId] = useState('') // Use empty string for "no change"
        const [isLoading, setIsLoading] = useState(false) // Add loading state

        useEffect(() => {
            // Set default values based on selected projects and phases from ProjectListStore
            if (selectedProjectIds.length > 0) {
                // Get selected project rows from queryData
                const selectedProjects = ProjectListStore.queryData.filter(
                    (project) => selectedProjectIds.includes(project.projectId)
                )

                if (selectedProjects.length > 0) {
                    // Check if all projects have the same costCentreId
                    const uniqueCostCentres = [
                        ...new Set(selectedProjects.map((p) => p.costCentreId)),
                    ]
                    if (
                        uniqueCostCentres.length === 1 &&
                        uniqueCostCentres[0]
                    ) {
                        setCostCentreId(uniqueCostCentres[0])
                    }

                    // Check if all projects have the same ownerId
                    const uniqueOwners = [
                        ...new Set(selectedProjects.map((p) => p.ownerId)),
                    ]
                    if (uniqueOwners.length === 1 && uniqueOwners[0]) {
                        setOwnerId(uniqueOwners[0])
                    }
                }
            }

            if (selectedPhaseIds.length > 0) {
                // Get all phases from selected projects' children
                const selectedPhases = ProjectListStore.queryData
                    .flatMap((project) => project.children || [])
                    .filter((phase) => selectedPhaseIds.includes(phase.phaseId))

                if (selectedPhases.length > 0) {
                    // Check if all phases have the same status
                    const uniqueStatuses = [
                        ...new Set(selectedPhases.map((p) => p.status)),
                    ]
                    if (uniqueStatuses.length === 1 && uniqueStatuses[0]) {
                        setStatus(uniqueStatuses[0])
                    }
                }
            }
        }, [selectedProjectIds, selectedPhaseIds])

        const handleSave = async () => {
            const updates = {}
            if (status) {
                updates.status = status
            }
            if (ownerId) {
                updates.ownerId = ownerId
                // Add owner display name
                const staff = StaffCollection.staffById[ownerId]
                if (staff) {
                    updates.owner = staff.fullName
                }
            }
            if (costCentreId) {
                updates.costCentreId = costCentreId
                // Add cost centre display name
                const costCentre =
                    CostCentreCollection.costCentresById[costCentreId]
                if (costCentre) {
                    updates.costCentre = costCentre.name
                }
            }

            if (!_.isEmpty(updates)) {
                setIsLoading(true)
                try {
                    const result =
                        await trpc.batchEditProjects.batchUpdate.mutate({
                            projectIds: selectedProjectIds,
                            phaseIds: selectedPhaseIds.length
                                ? selectedPhaseIds
                                : undefined,
                            updates: updates,
                        })

                    // Update the rows in ProjectListStore.queryData
                    ProjectListStore.queryData = ProjectListStore.queryData.map(
                        (project) => {
                            // If this project is selected, update its fields
                            if (
                                selectedProjectIds.includes(project.projectId)
                            ) {
                                return {
                                    ...project,
                                    ...updates,
                                    children: project.children?.map((phase) => {
                                        // If status is being updated and this phase is selected, update its status
                                        if (
                                            updates.status &&
                                            selectedPhaseIds.includes(
                                                phase.phaseId
                                            )
                                        ) {
                                            return {
                                                ...phase,
                                                status: updates.status,
                                            }
                                        }
                                        return phase
                                    }),
                                }
                            }
                            // If this project isn't selected but has selected phases, update those phases
                            else if (
                                project.children?.some((phase) =>
                                    selectedPhaseIds.includes(phase.phaseId)
                                )
                            ) {
                                return {
                                    ...project,
                                    children: project.children.map((phase) => {
                                        if (
                                            updates.status &&
                                            selectedPhaseIds.includes(
                                                phase.phaseId
                                            )
                                        ) {
                                            return {
                                                ...phase,
                                                status: updates.status,
                                            }
                                        }
                                        return phase
                                    }),
                                }
                            }
                            return project
                        }
                    )

                    // Clear selections after successful update
                    ProjectListStore.selectedProjectIds.clear()
                    ProjectListStore.selectedPhaseIds.clear()
                } catch (error) {
                    console.error('Failed to batch update projects:', error)
                } finally {
                    setIsLoading(false)
                }
            }
        }

        const title =
            selectedProjectIds.length > 0
                ? `Batch Edit ${selectedProjectIds.length} Project(s)`
                : `Batch Edit ${selectedPhaseIds.length} Phase(s)`

        return (
            <Modal
                modalId={modalId}
                heading={title}
                onSave={handleSave}
                saveLabel="Save Changes"
                isSaving={isLoading}
            >
                <div
                    className="p-[1em] grid gap-[0.5em]"
                    style={{
                        gridTemplateColumns:
                            selectedProjectIds.length > 0
                                ? '1fr 1fr 1fr'
                                : '1fr',
                    }}
                >
                    {selectedProjectIds.length > 0 && (
                        <>
                            <div>
                                <label
                                    htmlFor="project-cost-centre"
                                    className="text-sm text-[#444] font-bold"
                                >
                                    Cost Centre:
                                </label>
                                <div className="max-w-max">
                                    <CostCentreSelector
                                        id="project-cost-centre"
                                        selectedCostCentre={
                                            CostCentreCollection
                                                .costCentresById[costCentreId]
                                        }
                                        onChange={(costCentre) =>
                                            setCostCentreId(
                                                costCentre?.id || ''
                                            )
                                        }
                                        placeholderLabel="-- No Change --"
                                        className="w-full"
                                        nullable
                                        variant="secondary"
                                    />
                                </div>
                            </div>
                            <div>
                                <label
                                    htmlFor="project-owner"
                                    className="text-sm text-[#444] font-bold"
                                >
                                    Project Owner:
                                </label>
                                <div className="max-w-max">
                                    <StaffSelector
                                        id="project-owner"
                                        selectedStaffMember={
                                            StaffCollection.staffById[ownerId]
                                        }
                                        onChange={(staff) =>
                                            setOwnerId(staff?.id || '')
                                        }
                                        placeholderLabel="-- No Change --"
                                        className="w-full"
                                        nullable
                                        variant="secondary"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                    <div>
                        <label
                            htmlFor="project-status"
                            className="text-sm text-[#444] font-bold"
                        >
                            Status:
                        </label>
                        <div className="max-w-max">
                            <StatusSelector
                                id="project-status"
                                selectedStatus={status}
                                onChange={setStatus}
                                placeholder="-- No Change --"
                                className="w-full"
                                variant="secondary"
                            />
                        </div>
                    </div>
                </div>
            </Modal>
        )
    }
)

export default BatchEditProjectModal
